import { useState } from 'react';
import { Button, Checkbox, Form, Input, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import dayjs from 'dayjs';

import DatePicker from 'components/DatePicker/DatePicker';

import { formatDateToSubmit } from 'utilities/datesAndTimes';
import { BREAKPOINT } from 'utilities/constants';
import {
  ACTION_OPTIONS,
  ACTION_TYPE,
  CUSTOMER_ATTRIBUTE_OPTIONS,
  EXCLUSIVE_ACTIONS,
  STATUS_CODE_OPTIONS,
  ZENDESK_MACRO_OPTIONS,
  csvSpecForActions,
  csvTemplateHeader,
  isSecurityReportClosure,
  reasonCodeOptions,
} from './constants';

const FormGrid = styled.div`
  @media (min-width: ${BREAKPOINT.SM_PX}) {
    display: grid;
    grid-gap: 0 var(--spacing-md);
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas:
      'label actions actions actions'
      'statusCode reasonCode startDateET stopDateET'
      'securityReport securityReport securityReport securityReport'
      'macroId macroId . .'
      'customerInfoAttributes customerInfoAttributes . .'
      'file file file file';
  }
`;

const FormItem = styled(Form.Item)`
  grid-area: ${(props) => props.name};
`;

const asDate = (value) => (value ? formatDateToSubmit(value) : null);

/** Far enough out that the status does not lapse on its own. */
const NO_STOP_DATE = '2999-01-01';

/**
 * The fields are Eastern, so the browser's calendar date will not do: an agent
 * working the evening on the west coast would start the status on ET yesterday.
 */
const todayET = () =>
  dayjs(dayjs().tz('America/New_York').format('YYYY-MM-DD'));

/**
 * Checked against the status pair as well as the box. The form keeps a hidden
 * field's value, so a box ticked before the reason changed is still ticked.
 */
const wantsSecurityReport = (values) =>
  values.actions.includes(ACTION_TYPE.ACCOUNT_UPDATE) &&
  isSecurityReportClosure(values.statusCode, values.reasonCode) &&
  values.securityReport === true;

/**
 * An exclusive action cannot share an upload, so picking one replaces the others
 * and picking another replaces it. Applied as the field's `normalize`, so the
 * form never holds a combination the service would refuse.
 */
const enforceExclusiveAction = (selectedActions = [], previousActions = []) => {
  const newlySelectedAction = selectedActions.find(
    (action) => !previousActions.includes(action),
  );
  if (EXCLUSIVE_ACTIONS.includes(newlySelectedAction)) {
    return [newlySelectedAction];
  }
  return selectedActions.length > 1
    ? selectedActions.filter((action) => !EXCLUSIVE_ACTIONS.includes(action))
    : selectedActions;
};

/** What the file must hold, said the way the agent will check it. */
const csvInputHint = ({ requiredColumns, extraHint }) => {
  const columnDescriptions = requiredColumns.map(({ name, formatHint }) =>
    formatHint ? `${name} (${formatHint})` : name,
  );
  return [`Required columns: ${columnDescriptions.join(', ')}.`, extraHint]
    .filter(Boolean)
    .join(' ');
};

/** Collected alongside the file, for the reason `CREATE_BULK_UPLOAD` gives. */
const buildActions = (values) => {
  const actions = values.actions.map((type) => {
    switch (type) {
      case ACTION_TYPE.ACCOUNT_UPDATE:
        return {
          type,
          statusCode: values.statusCode,
          reasonCode: values.reasonCode,
          startDateET: asDate(values.startDateET),
          stopDateET: asDate(values.stopDateET),
        };
      case ACTION_TYPE.ZENDESK:
        return { type, macroId: values.macroId };
      case ACTION_TYPE.CUSTOMER_INFO_UPDATE:
        return { type, attributes: values.customerInfoAttributes };
      default:
        return { type };
    }
  });

  return wantsSecurityReport(values)
    ? [...actions, { type: ACTION_TYPE.SECURITY_REPORT }]
    : actions;
};

const BulkUploadForm = ({ onUpload, uploading }) => {
  const [form] = Form.useForm();
  // Taken at mount rather than at module load, so a tab left open overnight does
  // not start the status yesterday.
  const [initialValues] = useState(() => ({
    startDateET: todayET(),
    stopDateET: dayjs(NO_STOP_DATE),
  }));
  const actions = Form.useWatch('actions', form) ?? [];
  const statusCode = Form.useWatch('statusCode', form);
  const reasonCode = Form.useWatch('reasonCode', form);

  const needsStatus = actions.includes(ACTION_TYPE.ACCOUNT_UPDATE);
  const offersSecurityReport =
    needsStatus && isSecurityReportClosure(statusCode, reasonCode);
  const needsMacro = actions.includes(ACTION_TYPE.ZENDESK);
  const needsCustomerInfoAttributes = actions.includes(
    ACTION_TYPE.CUSTOMER_INFO_UPDATE,
  );
  const customerInfoAttributes = Form.useWatch('customerInfoAttributes', form);

  const csvSpec = csvSpecForActions(
    actions.map((type) => ({ type, attributes: customerInfoAttributes })),
  );
  const csvTemplateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(csvTemplateHeader(csvSpec))}`;

  const handleSubmit = (values) => {
    // beforeUpload returns false, so fileList holds antd UploadFile wrappers.
    const [picked] = values.file;

    const requestedActions = buildActions(values);

    onUpload(picked.originFileObj ?? picked, {
      sourceType: csvSpecForActions(requestedActions).sourceType,
      actions: requestedActions,
      label: values.label.trim(),
    });
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      initialValues={initialValues}
    >
      <FormGrid>
        <FormItem
          label="Label"
          name="label"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Give this upload a label',
            },
          ]}
          extra="How you will recognise this upload later."
        >
          <Input placeholder="e.g. Incident 2026-33" maxLength={80} />
        </FormItem>

        <FormItem
          label="Actions"
          name="actions"
          rules={[{ required: true, message: 'Choose at least one action' }]}
          normalize={enforceExclusiveAction}
          extra="Applied to every row in the file."
        >
          <Select
            mode="multiple"
            placeholder="Select the actions to apply"
            options={ACTION_OPTIONS}
          />
        </FormItem>

        {needsStatus && (
          <>
            <FormItem
              label="Status"
              name="statusCode"
              rules={[{ required: true, message: 'Status code is required' }]}
            >
              <Select
                placeholder="Select a status"
                options={STATUS_CODE_OPTIONS}
                onChange={() => form.setFieldValue('reasonCode', undefined)}
              />
            </FormItem>

            <FormItem
              label="Reason"
              name="reasonCode"
              rules={[{ required: true, message: 'Reason code is required' }]}
            >
              <Select
                placeholder={
                  statusCode ? 'Select a reason' : 'Pick a status first'
                }
                disabled={!statusCode}
                options={reasonCodeOptions(statusCode)}
              />
            </FormItem>

            <FormItem label="Start Date (ET)" name="startDateET">
              <DatePicker style={{ width: '100%' }} />
            </FormItem>

            <FormItem
              label="Stop Date (ET)"
              name="stopDateET"
              extra="Change it if the status should lapse."
            >
              <DatePicker style={{ width: '100%' }} />
            </FormItem>

            {offersSecurityReport && (
              <FormItem
                name="securityReport"
                valuePropName="checked"
                extra="Files an application-fraud security report on each account once it is closed."
              >
                <Checkbox>Apply SF/FA security report</Checkbox>
              </FormItem>
            )}
          </>
        )}

        {needsMacro && (
          <FormItem
            label="Zendesk Macro"
            name="macroId"
            rules={[{ required: true, message: 'Choose the macro to send' }]}
          >
            <Select
              placeholder="Select a macro"
              options={ZENDESK_MACRO_OPTIONS}
            />
          </FormItem>
        )}

        {needsCustomerInfoAttributes && (
          <FormItem
            label="Attributes"
            name="customerInfoAttributes"
            rules={[
              { required: true, message: 'Choose at least one attribute' },
            ]}
            extra="Each one is a column in the file, set on every customer in it."
          >
            <Select
              mode="multiple"
              placeholder="Select the attributes to update"
              options={CUSTOMER_ATTRIBUTE_OPTIONS}
            />
          </FormItem>
        )}

        <FormItem
          label="CSV File"
          name="file"
          valuePropName="fileList"
          getValueFromEvent={(event) => event?.fileList}
          rules={[{ required: true, message: 'Choose a CSV to upload' }]}
          extra={
            <>
              {csvInputHint(csvSpec)}{' '}
              <a
                href={csvTemplateHref}
                download={`${csvSpec.recordType}-template.csv`}
                data-testid="bulk-upload-template-link"
              >
                Download template
              </a>
            </>
          }
        >
          <Upload accept=".csv" maxCount={1} beforeUpload={() => false}>
            <Button icon={<UploadOutlined />}>Choose CSV</Button>
          </Upload>
        </FormItem>
      </FormGrid>

      <Button
        type="primary"
        htmlType="submit"
        loading={uploading}
        disabled={uploading}
        data-testid="bulk-upload-submit-button"
      >
        Upload File
      </Button>
    </Form>
  );
};

export default BulkUploadForm;
